import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, CircularProgress, FormControl, Grid, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import { GridDeleteIcon } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ISmenuFilho, MenuFilho, MenuFilhoList } from '../../../../../typesSite/menuFilho/MenuFilho';
import { useDeleteMenuFilhoMutation, useGetMenuFilhosQuery } from './menuFilhoApiSlice';
import ConfirmDialogList from '../../../../../components/ConfirmDialogList';
import configSiteUrls from '../../../configs/configSiteUrls';

export const MenuFilhoListForm = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [menuFilhos, setMenuFilhos] = useState<MenuFilhoList[]>([]);

  const [menuFilhoToDelete, setMenuFilhoToDelete] = useState<MenuFilho>(ISmenuFilho);
  const [openConfirmExcluir, setOpenConfirmExcluir] = useState(false);
  const [deleteMenuFilho, { error: errorMenuFilho, isLoading, isSuccess }] = useDeleteMenuFilhoMutation();

  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cursorPosition = useRef<number>(0);

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    cursorPosition.current = inputRef.current?.selectionStart || 0;
    setSearch(e.target.value);
  };

  useEffect(() => {
    // Limpar o timeout anterior
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Configurar o timeout para o debounce
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 700); // Tempo de debounce (700ms)

    // Limpar o timeout quando o componente for desmontado ou o efeito for reiniciado
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [search]); // Dependência do useEffect: quando o 'search' mudar

  const { data, isFetching, error } = useGetMenuFilhosQuery({
    search: debouncedSearch
  });

  useEffect(() => {
    if (data) {
      setMenuFilhos(data);
    }
    if (error) {
      enqueueSnackbar('MenuFilhos não encontradas!', { variant: 'error' });
    } else if (isFetching) {
      enqueueSnackbar('Buscando registros...', { variant: 'info' });
    }

    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current?.setSelectionRange(cursorPosition.current, cursorPosition.current);
    }
  }, [data, error, isFetching, enqueueSnackbar]);

  const headerGerados = (
    <Typography variant="body2" color="textSecondary">
      Menu(s) encontrado(s): {menuFilhos.length}
    </Typography>
  );

  const renderData = (rowData: MenuFilhoList) => {
    const dataCriacao = rowData.dataHoraCriacao ? format(rowData.dataHoraCriacao, 'dd/MM/yyyy HH:mm:ss') : 'Data não disponível';
    return (
      <Typography color="secondary" variant="body2">
        {dataCriacao}
      </Typography>
    );
  };

  const renderUsuario = (rowData: MenuFilhoList) => (
    <Typography color="secondary" variant="body2">
      {rowData.usuario?.nome || 'Usuário não disponível'}
    </Typography>
  );
  const renderDescricao = (rowData: MenuFilhoList) => {
    let descricao: string = '';     
     if (rowData.isEscolaLegislativo) {
      descricao = '(ESCOLA-LEG) ';
    }
    if (rowData.menuPai?.label) {
      descricao = '(' + rowData.menuPai.label + ') ';     
    }
    descricao += rowData.label ? rowData.label : '';
    return (
      <Typography color="secondary" variant="body2">
        {descricao}
      </Typography>
    );
  };
  const renderOrdem = (rowData: MenuFilhoList) => (
    <Typography color="secondary" variant="body2">
      {rowData.ordem || ''}
    </Typography>
  );

  const renderAction = (rowData: any) => {
    return (
      <Box sx={{ display: 'flex', gap: '8px' }}>
        <Tooltip title="Editar">
          <Link to={`${configSiteUrls.SITE_MENU_FILHO_EDIT_LK}${rowData.id}`}>
            <IconButton color="secondary" aria-label="edit">
              <EditIcon />
            </IconButton>
          </Link>
        </Tooltip>

        <Tooltip title="Excluir">
          <IconButton color="secondary" aria-label="delete" onClick={() => handleExcluirtClick(rowData)}>
            <GridDeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const handleExcluirtClick = (rowData: any) => {
    setMenuFilhoToDelete(rowData);
    setOpenConfirmExcluir(true);
  };
  const handleCloseDialog = () => {
    setOpenConfirmExcluir(false);
  };
  const handleConfirm = async () => {
    setOpenConfirmExcluir(false);
    await deleteMenuFilho({
      id: menuFilhoToDelete.id ? menuFilhoToDelete.id : ''
    });
  };

  useEffect(() => {
    if (isSuccess) {
      enqueueSnackbar('Registro deletado com sucesso!', { variant: 'success' });
      // fetchDados();
    }
    if (errorMenuFilho) {
      enqueueSnackbar('Registro não deletado!', { variant: 'error' });
    }
  }, [isSuccess, errorMenuFilho]);

  if (isFetching || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <ConfirmDialogList
        open={openConfirmExcluir}
        handleClose={handleCloseDialog}
        title="Confirmar Ação"
        text={['Você tem certeza de que deseja excluir esse SubMenu?', menuFilhoToDelete.label ? menuFilhoToDelete.label : '']}
        handleDelete={() => {
          handleConfirm(); // Chama a função handleConfirm
          setOpenConfirmExcluir(false); // Fecha o diálogo
        }}
      />

<Paper sx={{ width: '100%', height: '100vh', padding: '12px', display: 'flex', flexDirection: 'column' }}>
        <Grid container padding={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Box display="flex">
                  <Typography color="secondary" variant="h6" gutterBottom>
                    Sub Menu nível 2
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      navigate(configSiteUrls.SITE_MENU_FILHO_CREATE);
                    }}
                  >
                    Novo
                  </Button>

                  <Button variant="contained" color="secondary" onClick={() => navigate(-1)}>
                    Voltar
                  </Button>
                </Box>
              </Box>
            </FormControl>
          </Grid>

          <Grid item xs={12} mt={5}>
            <FormControl fullWidth>
              <TextField label="Digite para pesquisar" name="nome" onChange={handleChangeSearch} size="small" inputProps={{ maxLength: 100 }} value={search} inputRef={inputRef} />
            </FormControl>
          </Grid>
        </Grid>
       
        <Box sx={{ flex: 1, minHeight: 0 }}>
  <DataTable
    value={menuFilhos}
    paginator
    rows={25}
    rowsPerPageOptions={[10, 25, 50]}
    showGridlines
    size="small"
    dataKey="id"
    header={headerGerados}
    scrollable
    scrollHeight="flex"
    className="custom-datatable"
  >
    <Column header="Data" body={renderData} sortable style={{ width: '20%' }} />
    <Column header="Ordem" body={renderOrdem} sortable style={{ width: '10%' }} />
    <Column header="Descrição" body={renderDescricao} sortable />
    <Column header="Ações" body={renderAction} style={{ width: '15%' }} />
  </DataTable>
</Box>



      </Paper>
    </>
  );
};
